import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
    Activity, 
    CheckCircle, 
    AlertTriangle, 
    Clock, 
    Plus, 
    List, 
    ArrowRight, 
    BarChart2,
    PieChart
} from 'lucide-react';
import { APP_ROUTES } from '../routes';
import { useGetIncidents, useGetIncidentStats } from '../api/queries';
import { Incident } from '../types/incident';
import { getStatusColor } from '../utils/incidentUtils';
import Loader from '../components/Loader';
import StatCard from '../components/StatCard';

export default function Dashboard() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    
    const { data: stats, isLoading: statsLoading, error: statsError } = useGetIncidentStats();
    const { data: incidentsData, isLoading: incidentsLoading } = useGetIncidents(1, 5, 'created_at', 'desc');

    const incidents: Incident[] = incidentsData?.incidents || [];

    if (statsLoading && !stats) {
        return <Loader />;
    }

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                <p className="text-gray-500 mt-2">{t('dashboard.subtitle')}</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                 <button 
                    onClick={() => navigate(APP_ROUTES.SUBMIT_INCIDENT)}
                    className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors"
                >
                    <Plus className="mr-2" />
                    {t('dashboard.reportNew')}
                </button>
                <button 
                    onClick={() => navigate(APP_ROUTES.INCIDENT_LIST)}
                    className="flex items-center justify-center p-4 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                >
                    <List className="mr-2" />
                    {t('dashboard.viewLogs')}
                </button>
            </div>

            {/* Stats Grid */}
            {statsError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-8">
                    {statsError.message}
                </div>
            ) : stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard 
                        title={t('dashboard.totalIncidents')}
                        value={stats.total_incidents}
                        icon={Activity}
                        color="blue"
                    />
                    <StatCard 
                        title={t('dashboard.pending')}
                        value={stats.pending_incidents}
                        icon={AlertTriangle}
                        color="yellow"
                        subtext={t('dashboard.pendingSubtext')}
                    />
                    <StatCard 
                        title={t('dashboard.resolved')}
                        value={stats.resolved_incidents}
                        icon={CheckCircle}
                        color="green"
                    />
                    <StatCard 
                        title={t('dashboard.avgResolution')}
                        value={stats.average_resolution_time || 'N/A'}
                        icon={Clock}
                        color="purple"
                    />
                </div>
            )}

            {/* Analytics Row */}
            {stats && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center mb-4">
                            <PieChart className="text-indigo-500 mr-2" size={20}/>
                            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.commonCategory')}</h3>
                        </div>
                        <p className="text-3xl font-bold text-gray-800 capitalize">
                            {stats.most_common_category || 'None'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('dashboard.mostFrequent')}
                        </p>
                    </div>
                     <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center mb-4">
                            <BarChart2 className="text-green-500 mr-2" size={20}/>
                            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.resolutionRate')}</h3>
                        </div>
                        <p className="text-3xl font-bold text-gray-800">
                            {stats.total_incidents > 0 
                                ? Math.round((stats.resolved_incidents / stats.total_incidents) * 100) 
                                : 0}%
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('dashboard.percentageResolved')}
                        </p>
                    </div>
                 </div>
            )}

            {/* Recent Incidents */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.recentIncidents')}</h3>
                    <button 
                        onClick={() => navigate(APP_ROUTES.INCIDENT_LIST)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        {t('dashboard.viewAll')} <ArrowRight size={16} className="ml-1" />
                    </button>
                </div>
                {incidentsLoading ? (
                    <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
                ) : incidents.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">{t('dashboard.noIncidents')}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.priority')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.date')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {incidents.slice(0, 5).map((incident) => (
                                    <tr 
                                        key={incident.id} 
                                        onClick={() => navigate(APP_ROUTES.INCIDENT_DETAIL.replace(':id', incident.id))}
                                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{incident.title}</div>
                                            <div className="text-xs text-gray-500 truncate max-w-xs">{incident.description.substring(0, 50)}...</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(incident.status)}`}>
                                                {t(`status.${incident.status}`).toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                            {t(`priority.${incident.priority}`)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(incident.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
